import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  parseGbpOauthSearchParams,
  type GbpConnectErrorInfo,
} from '../lib/gbpConnectErrors';
import { clearGbpConnectSession, resetGbpConnectState } from '../lib/gbpConnectReset';
import {
  clearGbpOauthResume,
  GBP_RETURNING_FLAG,
  isGbpOauthReturning,
  stashGbpOauthResume,
} from '../lib/gbpOauthResume';
import GoogleBusinessConnectErrorModal from './GoogleBusinessConnectErrorModal';

export const GBP_CONNECT_RETURN_PATH_KEY = 'gbp_connect_return_path';
export const GBP_OPEN_SETTINGS_KEY = 'gbp_open_settings';
const GBP_CONNECT_BUSINESS_KEY = 'gbp_connect_business_id';
const GBP_CONNECT_PROFILE_KEY = 'gbp_connect_profile_id';

/**
 * Global handler for Zernio GBP OAuth redirects.
 * Stashes headless tokens, cleans the URL, and opens Integrations without a blank remount.
 */
export default function GoogleBusinessOauthReturnListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const handledReturn = useRef<string | null>(null);
  const [error, setError] = useState<GbpConnectErrorInfo | null>(null);
  const [finishing, setFinishing] = useState(() => isGbpOauthReturning());

  const resolveAppPath = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(GBP_CONNECT_RETURN_PATH_KEY);
      if (stored && stored.startsWith('/')) return stored.split('?')[0];
    } catch {
      /* ignore */
    }
    return '/app-store';
  }, []);

  const goBackToApp = useCallback(() => {
    const base = resolveAppPath();
    let feedbackConnect = false;
    try {
      feedbackConnect =
        sessionStorage.getItem('feedback_gbp_connect') === '1' ||
        new URLSearchParams(window.location.search).get('feedback_connect') === '1';
    } catch {
      /* ignore */
    }
    const returning = feedbackConnect
      ? 'gbp_returning=1&feedback_connect=1'
      : 'gbp_returning=1';
    const target =
      base === '/app-store' || base === '/integrations'
        ? `/app-store?tab=integrations&${returning}`
        : `${base}?${returning}`;
    const current = `${window.location.pathname}${window.location.search}`;
    // Already on the handoff URL. Setting the flag again would reopen the
    // connect UI the next time the user opens Marketing.
    if (current === target) return;
    try {
      sessionStorage.setItem(GBP_OPEN_SETTINGS_KEY, '1');
      // The query param is the handoff. Leaving the session flag set makes
      // every later route look like another Google return.
      sessionStorage.removeItem(GBP_RETURNING_FLAG);
    } catch {
      /* ignore */
    }
    navigateRef.current(target, { replace: true });
  }, [resolveAppPath]);

  useEffect(() => {
    const parsed = parseGbpOauthSearchParams(location.search);
    const returning = isGbpOauthReturning();

    if (!parsed.hasOauthReturn && !parsed.error && !returning) {
      handledReturn.current = null;
      setFinishing(false);
      return;
    }

    if (!parsed.hasOauthReturn && !parsed.error) {
      // Session flag only. Hand off once; a leftover flag must not pull the
      // user back onto the connect screen on every later navigation.
      if (handledReturn.current) return;
      handledReturn.current = 'session';
    } else {
      const handleId = `${location.pathname}${location.search}`;
      if (handledReturn.current === handleId) return;
      handledReturn.current = handleId;
    }

    if (parsed.error) {
      void (async () => {
        setFinishing(false);
        let keepFeedback = false;
        try {
          keepFeedback = sessionStorage.getItem('feedback_gbp_connect') === '1';
        } catch {
          /* ignore */
        }
        clearGbpOauthResume();
        const businessId = sessionStorage.getItem(GBP_CONNECT_BUSINESS_KEY) || '';
        if (businessId) {
          await resetGbpConnectState(businessId);
        } else {
          clearGbpConnectSession();
        }
        if (keepFeedback) {
          try {
            sessionStorage.setItem('feedback_gbp_connect', '1');
          } catch {
            /* ignore */
          }
        }
        setError(parsed.error);
        goBackToApp();
      })();
      return;
    }

    // Success / headless picker return
    setFinishing(true);
    const businessId = sessionStorage.getItem(GBP_CONNECT_BUSINESS_KEY) || '';
    const profileId =
      parsed.profileId || sessionStorage.getItem(GBP_CONNECT_PROFILE_KEY) || '';

    if (parsed.pendingDataToken && profileId) {
      stashGbpOauthResume({
        businessId,
        profileId,
        pendingDataToken: parsed.pendingDataToken,
        connectToken: parsed.connectToken,
      });
    }

    goBackToApp();

    // Overlay can drop once Integrations + ConnectButton take over.
    const t = window.setTimeout(() => setFinishing(false), 2500);
    return () => window.clearTimeout(t);
  }, [goBackToApp, location.pathname, location.search]);

  return (
    <>
      {finishing && !error ? (
        <div className="fixed inset-0 z-[100130] flex items-center justify-center bg-white/90">
          <div className="text-center px-6">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            <p className="text-sm font-semibold text-gray-900">Finishing Google connection…</p>
            <p className="text-xs text-gray-500 mt-1">Choose your Business Profile next</p>
          </div>
        </div>
      ) : null}
      <GoogleBusinessConnectErrorModal
        error={error}
        onClose={() => setError(null)}
        onRetry={() => {
          setError(null);
          goBackToApp();
        }}
      />
    </>
  );
}
